import { useSearchParams } from "react-router-dom";
import { SPORTS, SPORT_CONTENT } from "@/lib/biohackerBrand";

/**
 * useSportContext — reads ``?sport=<id>`` from the URL and returns the
 * matching sport metadata, or ``null`` when the visitor is on a global
 * (sport-neutral) page. Shared marketing pages (Pricing, Solutions,
 * Platform) call this to swap headline / bullet copy when they've been
 * deep-linked from a specific Sport Intelligence Package page.
 *
 * Baseball is intentionally handled by ``BaseballPackage.jsx`` so the
 * ``SPORT_CONTENT`` table only tracks the seven other sports.
 */
const NEUTRAL_HEADLINE   = "Every Athlete. Every Team.";
const NEUTRAL_TAGLINE    = "One Intelligence Platform.";
const NEUTRAL_WORKLOAD   = "Workload, recovery, AI insights";

const SPORT_HEADLINES = {
  baseball:   "Every Throw. Every Player.",
  softball:   "Every Windmill. Every Swing.",
  football:   "Every Snap. Every Down.",
  basketball: "Every Jump. Every Possession.",
  soccer:     "Every Sprint. Every Match.",
  volleyball: "Every Approach. Every Rally.",
  track:      "Every Split. Every Meet.",
  olympic:    "Every Cycle. Every Podium.",
};

const SPORT_WORKLOAD = {
  baseball:   "Throwing workload, recovery, AI insights",
  softball:   "Windmill workload, recovery, AI insights",
  football:   "Contact-load exposure, recovery, AI insights",
  basketball: "Jump load, court fatigue, recovery, AI insights",
  soccer:     "HSR + sprint load, recovery, AI insights",
  volleyball: "Jump volume, arm-strain, recovery, AI insights",
  track:      "Event volume, recovery, AI insights",
  olympic:    "Multi-sport load orchestration, recovery, AI insights",
};

export function useSportContext() {
  const [params] = useSearchParams();
  const sportId = params.get("sport");
  const sport = sportId && SPORTS.find((s) => s.id === sportId);
  if (!sport) {
    return {
      sport: null,
      isNeutral: true,
      copy: {
        headline: NEUTRAL_HEADLINE,
        tagline:  NEUTRAL_TAGLINE,
        workload: NEUTRAL_WORKLOAD,
      },
    };
  }
  const content = SPORT_CONTENT[sport.id] || null;
  return {
    sport,
    content,
    isNeutral: false,
    copy: {
      headline: SPORT_HEADLINES[sport.id] || NEUTRAL_HEADLINE,
      tagline:  NEUTRAL_TAGLINE,
      workload: SPORT_WORKLOAD[sport.id] || NEUTRAL_WORKLOAD,
    },
  };
}

/** Preserves ``?sport=...`` when building an internal link href. */
export function withSport(href, sport) {
  if (!sport) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}sport=${sport.id}`;
}
